import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'
import { listTickets, createTicket, addTicketMessage, getTicket } from '@/lib/coreApi'
import type { SupportContextMeta, SupportPanelProps } from './SupportPanel.types'
import NumaSummaryChip from './NumaSummaryChip'

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  in_progress: 'In progress',
  auto_resolved: 'Auto-resolved',
  closed: 'Closed',
}

type TicketMessage = {
  id: string
  body: string
  created_at: string
  sender_role: string
  visibility?: string | null
}

type TicketRecord = {
  id: string
  subject: string
  status: string
  priority: string
  category?: string | null
  created_at: string
  updated_at?: string | null
  ticket_messages?: TicketMessage[]
  metadata?: Record<string, unknown> | null
}

type InternalSupportPanelProps = SupportPanelProps & {
  className?: string
  intro?: string
  onClose?: () => void
  defaultMetadata?: Record<string, unknown> | null
  prospectId?: string | number | null
  context?: string | string[]
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleString()
}

function normalizeContextValues(value?: string | string[]) {
  if (!value) return []
  const values = Array.isArray(value) ? value : [value]
  return values.map((entry) => String(entry || '').trim()).filter(Boolean)
}

const DEFAULT_SURFACE: SupportContextMeta['surface'] = 'unknown'

export default function SupportPanel({
  roleLabel,
  defaultCategory,
  defaultPriority,
  contextMeta,
  emptyStateCopy,
  initialTicketId,
  defaultMetadata,
  prospectId,
  context,
  intro,
  className,
  onClose,
}: InternalSupportPanelProps) {
  const { session } = useAuth()
  const token = session?.access_token || ''

  const [tickets, setTickets] = useState<TicketRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<TicketRecord | null>(null)
  const [showComposer, setShowComposer] = useState(false)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [priority, setPriority] = useState<string>(defaultPriority || 'medium')
  const [category, setCategory] = useState<string>(defaultCategory || '')
  const [sending, setSending] = useState(false)
  const [reply, setReply] = useState('')

  useEffect(() => {
    setPriority(defaultPriority || 'medium')
  }, [defaultPriority])

  const normalizedContexts = useMemo(() => normalizeContextValues(context), [context])
  const normalizedContextMeta = useMemo<SupportContextMeta>(() => {
    if (contextMeta?.surface) {
      return contextMeta
    }
    return { ...(contextMeta || {}), surface: DEFAULT_SURFACE }
  }, [contextMeta])

  const mergedMetadata = useMemo(() => {
    const base: Record<string, unknown> = { ...normalizedContextMeta }
    if (defaultMetadata && Object.keys(defaultMetadata).length) {
      Object.assign(base, defaultMetadata)
    }
    if (prospectId != null && base.prospect_id == null) {
      base.prospect_id = prospectId
    }
    return base
  }, [normalizedContextMeta, defaultMetadata, prospectId])
  const headingLabel = roleLabel ? `${roleLabel} Support` : 'Support tickets'
  const introCopy =
    intro ||
    (roleLabel
      ? `The Tower team is here to help every ${roleLabel.toLowerCase()} in your program.`
      : 'Reach the Tower support team when you need help.')

  const sortedTickets = useMemo(() => {
    return [...tickets].sort((a, b) => {
      const left = new Date(a.updated_at || a.created_at).getTime()
      const right = new Date(b.updated_at || b.created_at).getTime()
      return right - left
    })
  }, [tickets])

  const [selectedMessages, setSelectedMessages] = useState<TicketMessage[]>([])

  const loadTickets = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const result = await listTickets({ scope: 'self' })
      const items: TicketRecord[] = (result?.data?.items as unknown as TicketRecord[]) || []
      setTickets(items)
      if (items.length) {
        setSelected((prev) => {
          const preferredId = initialTicketId || prev?.id
          if (preferredId) {
            const match = items.find((ticket) => ticket.id === preferredId)
            if (match) return match
          }
          if (prev) {
            const match = items.find((ticket) => ticket.id === prev.id)
            if (match) return match
          }
          return items[0]
        })
      } else {
        setSelected(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load tickets')
      setTickets([])
      setSelected(null)
    } finally {
      setLoading(false)
    }
  }, [token, initialTicketId])

  const loadSelectedMessages = useCallback(async () => {
    if (!token || !selected?.id) {
      setSelectedMessages([])
      return
    }
    try {
      const result = await getTicket(selected.id)
      setSelectedMessages((result.data.messages as unknown as TicketMessage[]) || [])
    } catch {
      // silent fail for messages
    }
  }, [token, selected?.id])

  useEffect(() => {
    loadTickets()
  }, [loadTickets])

  useEffect(() => {
    loadSelectedMessages()
  }, [loadSelectedMessages])

  useEffect(() => {
    if (!selected?.id) return
    const channel = supabase
      .channel(`support-ticket-${selected.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'ledger', table: 'ticket_messages', filter: `ticket_id=eq.${selected.id}` },
        () => loadSelectedMessages()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'ledger', table: 'tickets', filter: `id=eq.${selected.id}` },
        (payload) => {
          if (payload?.new) {
            setSelected((prev) => (prev && prev.id === payload.new.id ? { ...prev, ...payload.new } : prev))
          }
        }
      )

    channel.subscribe()
    return () => {
      channel.unsubscribe()
    }
  }, [selected?.id, loadSelectedMessages])

  useEffect(() => {
    setReply('')
  }, [selected?.id])

  const openComposer = () => {
    setSubject('')
    setMessage('')
    setPriority(defaultPriority || 'medium')
    setCategory(defaultCategory || '')
    setShowComposer(true)
  }

  const closeComposer = () => {
    setShowComposer(false)
  }

  const submitTicket = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!token) return
    setSending(true)
    try {
      const metadata: Record<string, unknown> = { ...mergedMetadata }
      if (normalizedContexts.length) {
        metadata.context = normalizedContexts
      }

      const result = await createTicket({
        subject,
        message,
        priority: priority as 'low' | 'medium' | 'high' | 'urgent',
        category: category || defaultCategory || 'general',
        metadata: Object.keys(metadata).length ? metadata : undefined,
      })
      setShowComposer(false)
      setSubject('')
      setMessage('')
      setPriority(defaultPriority || 'medium')
      setCategory(defaultCategory || '')
      await loadTickets()
      if (result.data.ticket) {
        setSelected(result.data.ticket as unknown as TicketRecord)
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Unable to submit ticket')
    } finally {
      setSending(false)
    }
  }

  const sendReply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!token || !selected?.id || !reply.trim()) return
    setSending(true)
    try {
      await addTicketMessage(selected.id, { body: reply })
      setReply('')
      await loadSelectedMessages()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Unable to send reply')
    } finally {
      setSending(false)
    }
  }

  if (!token) {
    return (
      <section className="Card">
        <p className="text-sm text-cocoa/70">Sign in to contact the support team.</p>
      </section>
    )
  }

  return (
    <div className={className || 'space-y-6'}>
      {showComposer ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-cocoa/40 px-4 py-8"
          role="presentation"
          onClick={closeComposer}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-cocoa">New ticket</h2>
              <button
                type="button"
                onClick={closeComposer}
                className="rounded-full border border-sand px-3 py-1 text-sm font-semibold text-cocoa hover:bg-shell"
              >
                Close
              </button>
            </div>
            <form onSubmit={submitTicket} className="space-y-3">
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Subject"
                className="w-full rounded-xl border border-sand px-3 py-2 text-sm"
                required
              />
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="How can we help?"
                className="min-h-[140px] w-full rounded-xl border border-sand px-3 py-2 text-sm"
                required
              />
              <div className="grid gap-3 md:grid-cols-2">
                <select
                  value={priority}
                  onChange={(event) => setPriority(event.target.value)}
                  className="w-full rounded-xl border border-sand px-3 py-2 text-sm"
                >
                  <option value="medium">Medium priority</option>
                  <option value="high">High priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="low">Low priority</option>
                </select>
                <input
                  type="text"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  placeholder="Category (optional)"
                  className="w-full rounded-xl border border-sand px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={sending} className="Button primary">
                  {sending ? 'Sending…' : 'Submit ticket'}
                </button>
                <button
                  type="button"
                  onClick={closeComposer}
                  className="Button ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <section className="Card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="Title">{headingLabel}</h2>
            <p className="text-sm text-cocoa/70">{introCopy}</p>
          </div>
          <div className="flex items-center gap-2">
            {onClose ? (
              <button type="button" onClick={onClose} className="Button ghost">
                Close
              </button>
            ) : null}
            <button type="button" onClick={openComposer} className="Button primary">
              New ticket
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-cocoa/70">Loading tickets…</p>
        ) : error ? (
          <p className="text-sm text-brick">{error}</p>
        ) : sortedTickets.length ? (
          <ul className="space-y-2">
            {sortedTickets.map((ticket) => (
              <li key={ticket.id}>
                <button
                  type="button"
                  onClick={() => setSelected(ticket)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    selected?.id === ticket.id
                      ? 'border-cocoa bg-cocoa/5 text-cocoa'
                      : 'border-sand bg-white text-cocoa/80 hover:bg-shell'
                  }`}
                >
                  <p className="font-semibold text-cocoa">{ticket.subject}</p>
                  <p className="text-xs uppercase tracking-wide text-cocoa/60">
                    {(STATUS_LABEL[ticket.status] || ticket.status || '').toUpperCase()} •{' '}
                    {PRIORITY_LABEL[ticket.priority] || ticket.priority || 'Medium'}
                  </p>
                  <p className="text-xs text-cocoa/60">Opened {formatDate(ticket.created_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        ) : emptyStateCopy ? (
          <div className="text-sm text-cocoa/70">{emptyStateCopy}</div>
        ) : (
          <p className="text-sm text-cocoa/70">No tickets yet.</p>
        )}
      </section>

      <section className="Card space-y-4">
        {selected ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-cocoa">{selected.subject}</h3>
                <p className="text-sm text-cocoa/60">
                  {(STATUS_LABEL[selected.status] || selected.status || '').toUpperCase()} •{' '}
                  {PRIORITY_LABEL[selected.priority] || 'Medium'} Priority
                </p>
              </div>
              <button type="button" onClick={openComposer} className="Button ghost">
                Start new ticket
              </button>
            </div>
            {token ? (
              <NumaSummaryChip ticketId={selected.id} token={token} className="w-full" />
            ) : null}
            <div className="space-y-3">
              {selectedMessages
                .filter((msg) => msg.visibility !== 'internal')
                .slice()
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-2xl border border-sand p-3 text-sm ${
                      msg.sender_role === 'numa' ? 'bg-emerald-50 text-emerald-900' : 'bg-shell/30 text-cocoa/80'
                    }`}
                  >
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">
                      {formatDate(msg.created_at)} •{' '}
                      {msg.sender_role === 'numa'
                        ? 'NUMA Assistant'
                        : msg.sender_role === 'admin'
                        ? 'Support team'
                        : 'You'}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-cocoa">{msg.body}</p>
                  </div>
                ))}
            </div>
            <form onSubmit={sendReply} className="space-y-3">
              <textarea
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder="Send a reply"
                className="min-h-[120px] w-full rounded-2xl border border-sand px-3 py-2 text-sm"
              />
              <button type="submit" disabled={!reply.trim() || sending} className="Button primary w-full sm:w-auto">
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </form>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-sm text-cocoa/70">
            <p>Select a ticket to view the conversation.</p>
            <button type="button" onClick={openComposer} className="Button ghost">
              Open a new ticket
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
